import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CheckCircle2, XCircle, AlertCircle, Loader2, Wifi, HelpCircle, Circle } from 'lucide-react';
import { ReachabilityStatus } from '../hooks/useUriReachability';
import { TestHostReachabilityResponse } from '@/lib/api/devices/reachability';
import { cn } from '@/lib/utils';

export interface ReachabilityIndicatorProps {
  status: ReachabilityStatus;
  result: TestHostReachabilityResponse | null;
  className?: string;
  /** Show a gray placeholder circle when status would normally be hidden (idle/cancelled) */
  showPlaceholder?: boolean;
}

export default function ReachabilityIndicator({
  status,
  result,
  className,
  showPlaceholder,
}: ReachabilityIndicatorProps) {
  // Don't show indicator for these statuses
  // 'invalid' is handled separately by UriInput with inline error text
  if (status === 'idle' || status === 'cancelled' || status === 'invalid') {
    if (showPlaceholder) {
      return (
        <div className={cn('flex items-center justify-center', className)}>
          <Circle className="h-4 w-4 text-gray-600" />
        </div>
      );
    }
    return null;
  }

  const getIcon = () => {
    switch (status) {
      case 'checking':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-400" />;
      case 'reachable':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'unreachable':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case 'untestable':
        return <HelpCircle className="h-4 w-4 text-gray-400" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
      default:
        return null;
    }
  };

  const getTooltipContent = () => {
    switch (status) {
      case 'checking':
        return <span>Checking host reachability...</span>;
      case 'reachable':
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3 text-green-400" />
              <span className="font-medium">Host is reachable</span>
            </div>
            {result && (
              <>
                <div className="text-xs text-gray-400">
                  {result.host}:{result.port}
                </div>
                <div className="text-xs text-gray-400">
                  Latency: {result.latencyMs}ms
                </div>
                {result.usedEdgeLink && (
                  <div className="flex items-center gap-1 text-xs text-teal-400">
                    <Wifi className="h-3 w-3" />
                    <span>Via EdgeLink</span>
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 'unreachable':
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <XCircle className="h-3 w-3 text-red-400" />
              <span className="font-medium">Host is unreachable</span>
            </div>
            {result?.errorMessage && (
              <div className="text-xs text-gray-400">{result.errorMessage}</div>
            )}
            {result && (
              <div className="text-xs text-gray-400">
                {result.host}:{result.port}
              </div>
            )}
            {result?.usedEdgeLink && (
              <div className="flex items-center gap-1 text-xs text-teal-400">
                <Wifi className="h-3 w-3" />
                <span>Via EdgeLink</span>
              </div>
            )}
          </div>
        );
      case 'untestable':
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-3 w-3 text-gray-400" />
              <span className="font-medium">Cannot test reachability</span>
            </div>
            {result?.untestableReason && (
              <div className="text-xs text-gray-400">{result.untestableReason}</div>
            )}
            {result && (
              <div className="text-xs text-gray-400">
                {result.host}:{result.port}
              </div>
            )}
          </div>
        );
      case 'error':
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-3 w-3 text-yellow-400" />
              <span className="font-medium">Failed to check reachability</span>
            </div>
            <div className="text-xs text-gray-400">
              An error occurred while testing the connection
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center justify-center cursor-help',
              className
            )}
          >
            {getIcon()}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
